import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAllocation,
  createPerson,
  createPersonAccount,
  createPersonResource,
  createTeamMember,
  deletePerson,
  deletePersonResource,
  getCapacity,
  getConflicts,
  getOrgChart,
  getPerson,
  getPersonActivity,
  getPersonAllocations,
  getPersonTasks,
  listAllocations,
  listPeople,
  listPersonResources,
  resendWelcome,
  setPersonDepartment,
  setPersonManager,
  updatePersonAccount,
  updatePersonContact,
  verifyPersonEmail,
  type AllocationsWindow,
  type CapacityWindow,
  type CreateAllocationInput,
  type CreatePersonAccountInput,
  type CreatePersonInput,
  type CreateTeamMemberInput,
  type PeopleFilters,
  type PersonResourceCategory,
  type UpdatePersonAccountInput,
  type UpdatePersonContactInput,
} from '../lib/peopleApi';

export function usePeople(filters: PeopleFilters = {}) {
  return useQuery({
    queryKey: ['people', filters],
    queryFn: () => listPeople(filters),
  });
}

export function usePerson(id: string | undefined) {
  return useQuery({
    queryKey: ['person', id],
    queryFn: () => getPerson(id as string),
    enabled: Boolean(id),
  });
}

export function usePersonAllocations(id: string | undefined) {
  return useQuery({
    queryKey: ['person', id, 'allocations'],
    queryFn: () => getPersonAllocations(id as string),
    enabled: Boolean(id),
  });
}

export function usePersonTasks(id: string | undefined) {
  return useQuery({
    queryKey: ['person', id, 'tasks'],
    queryFn: () => getPersonTasks(id as string),
    enabled: Boolean(id),
  });
}

export function usePersonActivity(id: string | undefined) {
  return useQuery({
    queryKey: ['person', id, 'activity'],
    queryFn: () => getPersonActivity(id as string),
    enabled: Boolean(id),
  });
}

export function useCapacity(window: CapacityWindow = {}) {
  return useQuery({
    queryKey: ['capacity', window],
    queryFn: () => getCapacity(window),
  });
}

/** Cross-people allocations overlapping a date window (calendar team Gantt). */
export function useTeamAllocations(window: AllocationsWindow = {}, enabled = true) {
  return useQuery({
    queryKey: ['allocations', window],
    queryFn: () => listAllocations(window),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useConflicts(window: CapacityWindow = {}) {
  return useQuery({
    queryKey: ['conflicts', window],
    queryFn: () => getConflicts(window),
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonInput) => createPerson(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/**
 * Create a team member with optional login account + RBAC (CLE-214 / CLE-215).
 * Invalidates the people lists and org chart so the directory and sidebar
 * reflect the new member.
 */
export function useCreateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTeamMemberInput) => createTeamMember(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/** Regenerate a member's password and resend the welcome email. */
export function useResendWelcome() {
  return useMutation({
    mutationFn: (personId: string) => resendWelcome(personId),
  });
}

/** Edit the person's contact fields + department (people.canEdit). */
export function useUpdatePersonContact(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePersonContactInput) => updatePersonContact(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', id] });
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

/** Provision a login account for an existing person (people.canManageRoles). */
export function useCreatePersonAccount(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonAccountInput) => createPersonAccount(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', id] });
    },
  });
}

/** Edit the linked login account (people.canManageRoles). */
export function useUpdatePersonAccount(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePersonAccountInput) => updatePersonAccount(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', id] });
    },
  });
}

/**
 * Force the linked account's email to verified (people.canManageRoles), for an
 * account stuck behind EMAIL_NOT_VERIFIED because the message never arrived.
 */
export function useVerifyPersonEmail(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => verifyPersonEmail(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', id] });
    },
  });
}

/**
 * Soft-delete a team member (people.canManageRoles). Deactivates the person and
 * any linked login account; the directory and detail views refresh afterwards.
 */
export function useDeletePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePerson(id),
    onSuccess: (_data, id) => {
      // Invalidating ['people'] also refreshes the org-chart query, which is
      // keyed under ['people', 'org-chart'].
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', id] });
    },
  });
}

// --- Org chart (CLE-70) ----------------------------------------------------

export function useOrgChart() {
  return useQuery({
    queryKey: ['people', 'org-chart'],
    queryFn: () => getOrgChart(),
  });
}

/**
 * Set or clear a person's manager. Invalidates the org chart and people lists
 * so the UI reflects the new hierarchy. The mutation variable is the target
 * person id + the manager id (or null to detach).
 */
export function useSetPersonManager() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, managerId }: { id: string; managerId: string | null }) =>
      setPersonManager(id, managerId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', vars.id] });
    },
  });
}

/**
 * Move a person into a department (or clear it with null). Invalidates the
 * people lists and the departments query (member counts change).
 */
export function useSetPersonDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, departmentId }: { id: string; departmentId: string | null }) =>
      setPersonDepartment(id, departmentId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', vars.id] });
      void qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

// --- Person resources -------------------------------------------------------
// Company-level documents attached directly to the person (no account needed).

const personResourcesKey = (id: string) => ['person', id, 'resources'] as const;

export function usePersonResources(personId: string | undefined) {
  return useQuery({
    queryKey: personResourcesKey(personId ?? ''),
    queryFn: () => listPersonResources(personId as string),
    enabled: Boolean(personId),
  });
}

export function useCreatePersonResource(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      category: PersonResourceCategory;
      s3Key: string;
      description?: string;
      fileType?: string;
      fileSize?: number;
    }) => createPersonResource(personId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: personResourcesKey(personId) });
    },
  });
}

export function useDeletePersonResource(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (resourceId: string) => deletePersonResource(resourceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: personResourcesKey(personId) });
    },
  });
}

export function useCreateAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAllocationInput) => createAllocation(input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person', vars.personId] });
      void qc.invalidateQueries({ queryKey: ['capacity'] });
      void qc.invalidateQueries({ queryKey: ['conflicts'] });
    },
  });
}
